// Content Studio — agency owners shape what their sub-account users see.
//  • My Videos: attach their own videos to our tutorials (add alongside / replace).
//  • Welcome Sequence (FTUX): an ordered list of docs/videos/tours shown to new users on first
//    visit (runs only when the FTUX toggle is on in Settings).
import { useEffect, useMemo, useState } from 'react';
import {
  agencyApi,
  type StudioAsset, type StudioVideo, type CatalogItem, type FtuxItem, type TourOverride,
  type AreaRef, type MyContentItem,
} from '../../lib/agencyApi';
import { allTours } from '../../widget/tours/index.js';
import { Button, Card, Chip, EmptyNote, ErrorNote, SectionHeader, Select, Spinner } from './ui';

const KIND_ICON: Record<string, string> = { doc: '📄', video: '▶️', tour: '🧭' };

export default function ContentStudio() {
  const [tab, setTab] = useState<'videos' | 'mine' | 'ftux' | 'tours' | 'notes'>('videos');
  const tabBtn = (id: typeof tab, label: string) => (
    <button className={`rounded-md px-3 py-1.5 ${tab === id ? 'bg-white font-medium shadow-sm' : 'text-slate-500'}`} onClick={() => setTab(id)}>{label}</button>
  );
  return (
    <div className="space-y-5">
      <div className="flex w-fit flex-wrap rounded-lg bg-slate-100 p-0.5 text-sm">
        {tabBtn('videos', 'My Videos')}
        {tabBtn('mine', 'My Content')}
        {tabBtn('ftux', 'Welcome Sequence')}
        {tabBtn('tours', 'Tour Text')}
        {tabBtn('notes', 'Doc Notes')}
      </div>
      {tab === 'videos' && <VideoStudio />}
      {tab === 'mine' && <MyContent />}
      {tab === 'ftux' && <FtuxBuilder />}
      {tab === 'tours' && <TourEditor />}
      {tab === 'notes' && <DocNoteEditor />}
    </div>
  );
}

// ── My Content — net-new agency-authored guides (docs, v1) ──────────────────────
// A guide the agency writes for a flow we don't cover. It lands in an area and shows to their
// sub-account users alongside ours. Markdown body; edit/delete anytime.
function MyContent() {
  const [items, setItems] = useState<MyContentItem[] | null>(null);
  const [areas, setAreas] = useState<AreaRef[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<MyContentItem | 'new' | null>(null);

  async function reload() {
    try {
      const [c, a] = await Promise.all([agencyApi.myContent(), agencyApi.studioAreas()]);
      setItems(c); setAreas(a);
    } catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void reload(); }, []);

  if (error) return <ErrorNote error={error} />;
  if (!items) return <Spinner />;

  if (editing) {
    return (
      <MyContentEditor
        item={editing === 'new' ? null : editing}
        areas={areas}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    );
  }

  const areaLabel = (id: string | null) => areas.find((a) => a.id === id)?.label ?? id ?? '—';

  return (
    <div className="space-y-4">
      <SectionHeader
        title="My content"
        subtitle="Write your own guides for the things we don't cover — your onboarding steps, an internal process, a policy. They appear to your clients alongside our articles, in the area you choose."
        action={<Button onClick={() => setEditing('new')}>+ New guide</Button>}
      />
      {items.length === 0 ? (
        <EmptyNote>No guides yet. Click “New guide” to write an article or add a video.</EmptyNote>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <Card key={it.id}>
              <div className="flex items-center justify-between gap-3">
                <button className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => setEditing(it)}>
                  <span aria-hidden>{it.kind === 'video' ? '▶️' : '📝'}</span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{it.title}</span>
                </button>
                <Chip label={it.kind === 'video' ? 'Video' : 'Article'} muted />
                <Chip label={areaLabel(it.feature_id)} muted />
                <Button variant="ghost" onClick={() => setEditing(it)}>Edit</Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function MyContentEditor({ item, areas, onClose, onSaved }: { item: MyContentItem | null; areas: AreaRef[]; onClose: () => void; onSaved: () => void }) {
  const isNew = !item;
  const [kind, setKind] = useState<'doc' | 'video'>(item?.kind ?? 'doc');
  const [title, setTitle] = useState(item?.title ?? '');
  const [area, setArea] = useState(item?.feature_id ?? areas[0]?.id ?? '');
  const [body, setBody] = useState(item?.markdown ?? '');
  // Video fields.
  const [vtab, setVtab] = useState<'url' | 'file'>('url');
  const [url, setUrl] = useState(item?.video_source === 'embed' ? (item.video_ref ?? '') : '');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setErr(null);
    if (!title.trim()) { setErr('Give your guide a title.'); return; }
    if (!area) { setErr('Choose an area.'); return; }
    setBusy(true);
    try {
      if (kind === 'doc') {
        if (item) await agencyApi.updateContent(item.id, { title: title.trim(), area, body });
        else await agencyApi.createDoc({ title: title.trim(), area, body });
      } else {
        // Video: create or update the asset, then (re)attach the video if one was provided.
        if (item) {
          await agencyApi.updateContent(item.id, { title: title.trim(), area });
          if (vtab === 'file' && file) await agencyApi.replaceVideoUpload(item.id, file);
          else if (vtab === 'url' && url.trim() && url.trim() !== item.video_ref) await agencyApi.replaceVideoEmbed(item.id, url.trim());
        } else if (vtab === 'file') {
          if (!file) throw new Error('Choose a video file to upload.');
          await agencyApi.createVideoUpload({ title: title.trim(), area, file });
        } else {
          if (!url.trim()) throw new Error('Paste a video link.');
          await agencyApi.createVideoEmbed({ title: title.trim(), area, url: url.trim() });
        }
      }
      onSaved();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }
  async function remove() {
    if (!item) return;
    setBusy(true); setErr(null);
    try { await agencyApi.deleteContent(item.id); onSaved(); }
    catch (e) { setErr((e as Error).message); setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-800">← My content</button>
        <span className="flex items-center gap-2">
          {item && <Button variant="ghost" onClick={remove} disabled={busy}>Delete</Button>}
          <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : item ? 'Save changes' : 'Create guide'}</Button>
        </span>
      </div>
      <Card>
        {isNew && (
          <>
            <label className="mb-1 block text-xs font-medium text-slate-500">Type</label>
            <div className="mb-3 flex w-fit rounded-lg bg-slate-100 p-0.5 text-sm">
              <button className={`rounded px-3 py-1 ${kind === 'doc' ? 'bg-white shadow-sm' : 'text-slate-500'}`} onClick={() => setKind('doc')}>📝 Article</button>
              <button className={`rounded px-3 py-1 ${kind === 'video' ? 'bg-white shadow-sm' : 'text-slate-500'}`} onClick={() => setKind('video')}>▶️ Video</button>
            </div>
          </>
        )}
        <label className="mb-1 block text-xs font-medium text-slate-500">Title</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={kind === 'video' ? 'e.g. Our custom onboarding walkthrough' : 'e.g. How we onboard new clients'}
          className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
        <label className="mb-1 block text-xs font-medium text-slate-500">Area</label>
        <Select value={area} onChange={setArea}>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
        </Select>

        {kind === 'doc' ? (
          <>
            <label className="mb-1 mt-3 block text-xs font-medium text-slate-500">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your guide in Markdown — # headings, **bold**, *italic*, `code`, - bullets, [links](https://…)."
              rows={16}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
            <p className="mt-1 text-xs text-slate-400">Markdown supported. Use <code>{'{{brandName}}'}</code> to insert your brand name automatically.</p>
          </>
        ) : (
          <div className="mt-3 space-y-2.5">
            {item?.video_ref && (
              <p className="text-xs text-slate-500">Current video: {item.video_source === 'embed' ? item.video_ref : 'uploaded file'}. Attach a new one below to replace it.</p>
            )}
            <div className="flex rounded-lg bg-slate-100 p-0.5 text-xs">
              <button className={`rounded px-2 py-1 ${vtab === 'url' ? 'bg-white shadow-sm' : 'text-slate-500'}`} onClick={() => setVtab('url')}>Paste a link</button>
              <button className={`rounded px-2 py-1 ${vtab === 'file' ? 'bg-white shadow-sm' : 'text-slate-500'}`} onClick={() => setVtab('file')}>Upload a file</button>
            </div>
            {vtab === 'url' ? (
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://loom.com/share/…  ·  youtube  ·  vimeo  ·  a direct .mp4"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              />
            ) : (
              <input
                type="file"
                accept="video/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-slate-200"
              />
            )}
            {vtab === 'file' && <p className="text-xs text-slate-400">Files up to 100 MB. For larger videos, host on Loom / YouTube / Vimeo and paste the link.</p>}
          </div>
        )}
        {err && <p className="mt-2 text-sm text-rose-600">{err}</p>}
      </Card>
    </div>
  );
}

// ── Doc notes editor ────────────────────────────────────────────────────────────
function DocNoteEditor() {
  const [docs, setDocs] = useState<CatalogItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [slug, setSlug] = useState('');
  const [note, setNote] = useState('');
  const [initial, setInitial] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    agencyApi.studioCatalog()
      .then((c) => setDocs(c.filter((x) => x.kind === 'doc')))
      .catch((e) => setError((e as Error).message));
  }, []);

  function pick(s: string) {
    setSlug(s); setSaved(false); setLoading(true);
    agencyApi.docNote(s)
      .then((r) => { setNote(r.note ?? ''); setInitial(r.note ?? ''); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }
  async function save() {
    setSaving(true); setError(null);
    try { const r = await agencyApi.saveDocNote(slug, note); setNote(r.note ?? ''); setInitial(r.note ?? ''); setSaved(true); }
    catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  }

  if (error) return <ErrorNote error={error} />;
  if (!docs) return <Spinner />;

  const dirty = note !== initial;
  const needle = q.trim().toLowerCase();
  const list = needle ? docs.filter((d) => `${d.title} ${d.area ?? ''}`.toLowerCase().includes(needle)) : docs;
  const selected = docs.find((d) => d.doc_slug === slug);

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Doc notes"
        subtitle="Add your own note to the top of any of our articles — your process, a caveat, extra context. Markdown supported (**bold**, *italic*, - lists, [links](url)). Ours stays below, unchanged."
      />
      {!slug ? (
        <>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" className="w-full max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand" />
          {!list.length && <EmptyNote>No articles match “{q}”.</EmptyNote>}
          <div className="max-h-[28rem] space-y-1 overflow-y-auto pr-1">
            {list.slice(0, 300).map((d) => (
              <button key={d.id} onClick={() => pick(d.doc_slug!)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50">
                <span aria-hidden>📄</span>
                <span className="min-w-0 flex-1 truncate text-slate-700">{d.title}</span>
                {d.area && <span className="hidden text-xs text-slate-400 md:inline">{d.area}</span>}
              </button>
            ))}
          </div>
        </>
      ) : (
        <Card>
          <div className="mb-3 flex items-center justify-between gap-3">
            <button onClick={() => { setSlug(''); setNote(''); setInitial(''); }} className="text-sm text-slate-500 hover:text-slate-800">← All articles</button>
            <span className="flex items-center gap-2">
              {saved && !dirty && <span className="text-xs text-emerald-600">Saved</span>}
              <Button onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : note.trim() ? 'Save note' : 'Remove note'}</Button>
            </span>
          </div>
          <div className="mb-2 text-sm font-medium text-slate-800">{selected?.title ?? slug}</div>
          {loading ? <Spinner /> : (
            <textarea
              value={note}
              onChange={(e) => { setNote(e.target.value); setSaved(false); }}
              placeholder="Write a note for your clients — it appears in a callout at the top of this article. Leave blank to remove."
              rows={7}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            />
          )}
          <p className="mt-1 text-xs text-slate-400">Markdown: **bold**, *italic*, `code`, - bullets, [text](https://…). Renders as a callout above our article.</p>
        </Card>
      )}
    </div>
  );
}

// ── Tour step-text editor ──────────────────────────────────────────────────────
type MasterStep = { title: string; description: string };
function tourList(): { id: string; name: string }[] {
  return Object.entries(allTours as Record<string, { name?: string; displayName?: string }>)
    .map(([id, t]) => ({ id, name: t.displayName || t.name || id }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
function masterSteps(tourId: string): MasterStep[] {
  const t = (allTours as Record<string, { steps?: Array<{ popover?: { title?: string; description?: string } }> }>)[tourId];
  return (t?.steps ?? []).map((s) => ({ title: (s.popover?.title ?? '').trim(), description: (s.popover?.description ?? '').replace(/\s+/g, ' ').trim() }));
}

function TourEditor() {
  const tours = useMemo(tourList, []);
  const [tour, setTour] = useState(tours[0]?.id ?? '');
  const [ov, setOv] = useState<TourOverride>({});
  const [initial, setInitial] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!tour) return;
    setLoading(true); setSaved(false);
    agencyApi.tourOverlay(tour)
      .then((r) => { setOv(r.steps ?? {}); setInitial(JSON.stringify(r.steps ?? {})); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [tour]);

  const steps = masterSteps(tour);
  const dirty = JSON.stringify(ov) !== initial;

  const setField = (i: number, field: 'title' | 'description', value: string) => {
    setOv((prev) => {
      const next = { ...prev };
      const cur = { ...(next[i] ?? {}) };
      if (value.trim()) cur[field] = value; else delete cur[field];
      if (Object.keys(cur).length) next[i] = cur; else delete next[i];
      return next;
    });
    setSaved(false);
  };

  async function save() {
    setSaving(true); setError(null);
    try {
      const r = await agencyApi.saveTourOverlay(tour, ov);
      setOv(r.steps ?? {}); setInitial(JSON.stringify(r.steps ?? {})); setSaved(true);
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  }

  if (error) return <ErrorNote error={error} />;

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Tour text"
        subtitle="Rewrite any step of a guided tour in your own words. Leave a field blank to keep ours. Your edits show to your clients; ours stay the fallback."
        action={
          <span className="flex items-center gap-2">
            {saved && !dirty && <span className="text-xs text-emerald-600">Saved</span>}
            <Button onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save changes'}</Button>
          </span>
        }
      />
      <div className="flex items-center gap-2 text-sm">
        <span className="text-slate-500">Tour:</span>
        <Select value={tour} onChange={setTour}>
          {tours.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
      </div>

      {loading ? <Spinner /> : steps.length === 0 ? (
        <EmptyNote>This tour has no editable steps.</EmptyNote>
      ) : (
        <div className="space-y-3">
          {steps.map((s, i) => {
            const o = ov[i] ?? {};
            return (
              <Card key={i}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">{i + 1}</span>
                  {(o.title || o.description) && <Chip label="Customized" color="#0369a1" />}
                </div>
                <label className="mb-1 block text-xs font-medium text-slate-500">Title</label>
                <input
                  value={o.title ?? ''}
                  onChange={(e) => setField(i, 'title', e.target.value)}
                  placeholder={s.title || 'Step title'}
                  className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                />
                <label className="mb-1 block text-xs font-medium text-slate-500">Description</label>
                <textarea
                  value={o.description ?? ''}
                  onChange={(e) => setField(i, 'description', e.target.value)}
                  placeholder={s.description || 'Step description'}
                  rows={3}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
                />
                {s.description && !o.description && <p className="mt-1 text-xs text-slate-400">Ours: {s.description.slice(0, 140)}{s.description.length > 140 ? '…' : ''}</p>}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Welcome Sequence (FTUX) ────────────────────────────────────────────────────
function FtuxBuilder() {
  const [catalog, setCatalog] = useState<CatalogItem[] | null>(null);
  const [seq, setSeq] = useState<string[]>([]);     // ordered asset ids
  const [initial, setInitial] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [cat, items] = await Promise.all([agencyApi.studioCatalog(), agencyApi.ftuxSequence()]);
        setCatalog(cat);
        const ids = items.sort((a, b) => a.position - b.position).map((i: FtuxItem) => i.asset_id);
        setSeq(ids);
        setInitial(ids.join(','));
      } catch (e) { setError((e as Error).message); }
    })();
  }, []);

  const byId = useMemo(() => new Map((catalog ?? []).map((c) => [c.id, c])), [catalog]);
  const dirty = seq.join(',') !== initial;

  if (error) return <ErrorNote error={error} />;
  if (!catalog) return <Spinner />;

  const inSeq = new Set(seq);
  const needle = q.trim().toLowerCase();
  const available = catalog.filter((c) => !inSeq.has(c.id) && (!needle || `${c.title} ${c.area ?? ''} ${c.kind}`.toLowerCase().includes(needle)));

  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= seq.length) return;
    const next = seq.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setSeq(next); setSaved(false);
  };
  const remove = (id: string) => { setSeq(seq.filter((x) => x !== id)); setSaved(false); };
  const add = (id: string) => { setSeq([...seq, id]); setSaved(false); };

  async function save() {
    setSaving(true); setError(null);
    try {
      await agencyApi.saveFtux(seq);
      setInitial(seq.join(',')); setSaved(true);
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Welcome sequence"
        subtitle="Build the first-time experience: pick the guides, videos, and tours a new sub-account user sees, in order. It runs once per user — only when the Welcome tour (FTUX) toggle is on in Settings."
        action={
          <span className="flex items-center gap-2">
            {saved && !dirty && <span className="text-xs text-emerald-600">Saved</span>}
            <Button onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save sequence'}</Button>
          </span>
        }
      />

      <Card>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">In your welcome sequence ({seq.length})</div>
        {seq.length === 0 ? (
          <EmptyNote>Nothing yet — add items from below to build the welcome flow.</EmptyNote>
        ) : (
          <ol className="space-y-1.5">
            {seq.map((id, i) => {
              const c = byId.get(id);
              return (
                <li key={id} className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <span className="w-5 text-center text-slate-400">{i + 1}</span>
                  <span aria-hidden>{KIND_ICON[c?.kind ?? ''] ?? '•'}</span>
                  <span className="min-w-0 flex-1 truncate text-slate-700">{c?.title ?? id}</span>
                  {c?.area && <span className="hidden text-xs text-slate-400 sm:inline">{c.area}</span>}
                  <button className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">↑</button>
                  <button className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" disabled={i === seq.length - 1} onClick={() => move(i, 1)} aria-label="Move down">↓</button>
                  <button className="px-1 text-slate-400 hover:text-rose-600" onClick={() => remove(id)} aria-label="Remove">✕</button>
                </li>
              );
            })}
          </ol>
        )}
      </Card>

      <Card>
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Add items</div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-48 rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand" />
        </div>
        {available.length === 0 ? (
          <EmptyNote>{needle ? `Nothing matches “${q}”.` : 'Everything is already in your sequence.'}</EmptyNote>
        ) : (
          <div className="max-h-96 space-y-1 overflow-y-auto pr-1">
            {available.slice(0, 200).map((c) => (
              <button key={c.id} onClick={() => add(c.id)} className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm hover:bg-slate-50">
                <span aria-hidden>{KIND_ICON[c.kind]}</span>
                <span className="min-w-0 flex-1 truncate text-slate-700">{c.title}</span>
                <Chip label={c.kind} muted />
                {c.area && <span className="hidden text-xs text-slate-400 md:inline">{c.area}</span>}
                <span className="text-brand">+ Add</span>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ── My Videos (BYO) ────────────────────────────────────────────────────────────
function VideoStudio() {
  const [assets, setAssets] = useState<StudioAsset[] | null>(null);
  const [videos, setVideos] = useState<StudioVideo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  async function reload() {
    try {
      const [a, v] = await Promise.all([agencyApi.studioAssets(), agencyApi.studioVideos()]);
      setAssets(a);
      setVideos(v);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  useEffect(() => { void reload(); }, []);

  const byAsset = useMemo(() => {
    const m = new Map<string, StudioVideo[]>();
    for (const v of videos) { const arr = m.get(v.asset_id) ?? []; arr.push(v); m.set(v.asset_id, arr); }
    return m;
  }, [videos]);

  if (error) return <ErrorNote error={error} />;
  if (!assets) return <Spinner />;

  const needle = q.trim().toLowerCase();
  const filtered = needle ? assets.filter((a) => `${a.title} ${a.area ?? ''}`.toLowerCase().includes(needle)) : assets;
  const groups = new Map<string, StudioAsset[]>();
  for (const a of filtered) { const k = a.area ?? 'Other'; const arr = groups.get(k) ?? []; arr.push(a); groups.set(k, arr); }

  return (
    <div className="space-y-5">
      <SectionHeader
        title="Your videos"
        subtitle="Attach your own videos to any tutorial — add them alongside ours, or replace ours for your clients. Paste a Loom / YouTube / Vimeo link, or upload a file."
      />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search tutorials…"
        className="w-full max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
      />
      {!filtered.length && <EmptyNote>No tutorials match “{q}”.</EmptyNote>}
      {[...groups.entries()].map(([area, items]) => (
        <div key={area}>
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">{area}</div>
          <div className="space-y-2">
            {items.map((a) => {
              const mine = byAsset.get(a.id) ?? [];
              const open = openId === a.id;
              return (
                <Card key={a.id}>
                  <button className="flex w-full items-center justify-between text-left" onClick={() => setOpenId(open ? null : a.id)}>
                    <span className="text-sm font-medium text-slate-800">{a.title}</span>
                    <span className="flex items-center gap-2">
                      {mine.length > 0 && <Chip label={`${mine.length} of yours`} color="#0369a1" />}
                      <span className="text-slate-400">{open ? '▲' : '▼'}</span>
                    </span>
                  </button>
                  {open && <AssetPanel asset={a} mine={mine} onChange={reload} />}
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function AssetPanel({ asset, mine, onChange }: { asset: StudioAsset; mine: StudioVideo[]; onChange: () => void }) {
  const [mode, setMode] = useState<'add' | 'replace'>('add');
  const [tab, setTab] = useState<'url' | 'file'>('url');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setErr(null);
    setBusy(true);
    try {
      if (tab === 'url') {
        if (!url.trim()) throw new Error('Paste a video link first.');
        await agencyApi.attachStudioVideo({ asset_id: asset.id, mode, url: url.trim(), title: title.trim() || undefined });
      } else {
        if (!file) throw new Error('Choose a video file first.');
        await agencyApi.uploadStudioVideo({ asset_id: asset.id, mode, file, title: title.trim() || undefined });
      }
      setUrl(''); setTitle(''); setFile(null);
      onChange();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setErr(null);
    try { await agencyApi.deleteStudioVideo(id); onChange(); } catch (e) { setErr((e as Error).message); }
  }

  return (
    <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
      {mine.length > 0 && (
        <div className="space-y-1.5">
          {mine.map((v) => (
            <div key={v.id} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <span className="flex min-w-0 items-center gap-2">
                <Chip label={v.mode === 'replace' ? 'Replaces ours' : 'Added'} color={v.mode === 'replace' ? '#b45309' : '#0369a1'} />
                <span className="truncate text-slate-700">{v.title || (v.source === 'embed' ? v.media_ref : 'Uploaded video')}</span>
                <span className="shrink-0 text-xs text-slate-400">{v.source}</span>
              </span>
              <Button variant="ghost" onClick={() => remove(v.id)}>Remove</Button>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2.5 rounded-lg border border-slate-200 p-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-500">Attach a video:</span>
          <Select value={mode} onChange={(v) => setMode(v as 'add' | 'replace')}>
            <option value="add">Add alongside ours</option>
            <option value="replace">Replace ours</option>
          </Select>
          <div className="ml-auto flex rounded-lg bg-slate-100 p-0.5 text-xs">
            <button className={`rounded px-2 py-1 ${tab === 'url' ? 'bg-white shadow-sm' : 'text-slate-500'}`} onClick={() => setTab('url')}>Paste a link</button>
            <button className={`rounded px-2 py-1 ${tab === 'file' ? 'bg-white shadow-sm' : 'text-slate-500'}`} onClick={() => setTab('file')}>Upload a file</button>
          </div>
        </div>
        {tab === 'url' ? (
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://loom.com/share/…  ·  youtube  ·  vimeo  ·  a direct .mp4"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          />
        ) : (
          <input
            type="file"
            accept="video/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-slate-200"
          />
        )}
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (optional)"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
        {tab === 'file' && <p className="text-xs text-slate-400">Files up to 100 MB. For larger videos, host on Loom / YouTube / Vimeo and paste the link.</p>}
        {err && <p className="text-xs text-rose-600">{err}</p>}
        <Button onClick={submit} disabled={busy}>{busy ? 'Attaching…' : 'Attach video'}</Button>
      </div>
    </div>
  );
}
